import type { ReactNode, RefAttributes } from 'react';
import { useContext, useRef } from 'react';
import type {
  PopoverProps as AriaPopoverProps,
  PopoverRenderProps,
} from 'react-aria-components';
import {
  OverlayTriggerStateContext,
  Popover as AriaPopover,
  PopoverContext,
} from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { PortalEventBoundary } from '@/lib/base/overlays/portal-event-boundary';
import { useDismissOnInteractOutside } from '@/lib/base/overlays/useDismissOnInteractOutside';
import { cx } from '@/utils/cx';

interface PopoverProps
  extends Omit<AriaPopoverProps, 'children'>, RefAttributes<HTMLElement> {
  /**
   * Controls the panel's max height, not the trigger's dimensions. It accepts
   * the same five steps as the form-control size scale so a Select can forward
   * its own `size` straight through; everything from `md` up gets the taller
   * panel. Intentionally spelled out rather than importing `SelectSize` —
   * Popover is a generic overlay and shouldn't depend on the forms module.
   */
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  children?: ReactNode;
}

const TALL_PANEL_SIZES = new Set(['md', 'lg', 'xl']);

/** Stable no-op for the dismiss hook when no trigger state is in context. */
const NOOP_DISMISS = () => {};

export const Popover = ({ children, ...props }: PopoverProps) => {
  const zIndex = useStackingZIndex();
  // Non-modal popovers disable react-aria's built-in outside-pointer
  // dismissal; restore it here (full rationale in
  // useDismissOnInteractOutside).
  const popoverRef = useRef<HTMLElement>(null);
  const triggerState = useContext(OverlayTriggerStateContext);
  const popoverCtx = useContext(PopoverContext);
  // ComboBox and Select manage their own open/close logic (blur-based for
  // ComboBox; button-toggle for Select). Applying useDismissOnInteractOutside
  // to them breaks the "own trigger" detection: the hook sees the input/button
  // has aria-controls pointing into the popover, swallows the pointerdown, and
  // closes the menu — and since menuTrigger='focus' doesn't reopen on click,
  // the listbox stays closed for the rest of the interaction.
  const isManagedTrigger =
    (popoverCtx as { trigger?: string } | null)?.trigger === 'ComboBox' ||
    (popoverCtx as { trigger?: string } | null)?.trigger === 'Select';
  useDismissOnInteractOutside(
    popoverRef,
    isManagedTrigger ? false : (triggerState?.isOpen ?? false),
    triggerState?.close ?? NOOP_DISMISS
  );
  return (
    // The boundary must wrap the AriaPopover ELEMENT (not its children):
    // react-aria's own Escape/keyboard handling lives on the popover root, and
    // React only skips ancestors of the boundary — see portal-event-boundary.
    <PortalEventBoundary>
      <AriaPopover
        placement='bottom'
        containerPadding={0}
        offset={4}
        // Non-modal by default — see DropdownPopover (dropdown.tsx) for the full
        // rationale: the modal underlay renders at z-index auto and paints
        // beneath stacked surfaces (slideouts), so outside-click dismissal never
        // fires there. Overridable via props (spread is after this default).
        isNonModal
        {...props}
        // After the spread on purpose: the dismiss hook's ref must win — a
        // caller-supplied ref would silently disable outside-click dismissal.
        // (No consumer passes one today; support merging if that changes.)
        ref={popoverRef}
        className={(state: PopoverRenderProps) =>
          cx(
            'max-h-64! min-w-(--trigger-width) w-max origin-(--trigger-anchor-point) overflow-x-hidden overflow-y-auto rounded-lg bg-primary py-1 shadow-lg ring-1 ring-secondary_alt outline-hidden will-change-transform',

            state.isEntering &&
              'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
            state.isExiting &&
              'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5',
            TALL_PANEL_SIZES.has(props.size) && 'max-h-80!',

            typeof props.className === 'function'
              ? props.className(state as never)
              : props.className
          )
        }
        style={{ zIndex, ...props.style }}
      >
        <StackingLayer>{children}</StackingLayer>
      </AriaPopover>
    </PortalEventBoundary>
  );
};
